import React, { useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button"; // Import Button component
import CreateAnnouncementCard from "../../components/CreateAnnouncementCard";
import TherapistInfo from "../../components/TherapistInfo";
// import ProfileProgressCard from "../../components/ProfileProgressCard";
import InviteCard from "../../components/InviteCard";
import UserVerticalCard from "../../components/UserVerticalCard";
import PostCard from "../../components/PostCard";
// import ConnectionsCard from "../../components/ConnectionsCard";
import MarketingCard from "../../components/MarketingCard";
import { useHomePage } from "../../contexts/HomePage";
import PostForm from "../../components/PostForm";
import {
  LOADING,
  SUCCESS,
  colleagues,
  treatment_program,
} from "../../constants/common";
import CardLoader from "../../components/CardLoader";
import { useBasicInfo } from "../../contexts/BasicInfo";
import VerificationModal from "../../components/VerificationModal";
import ReferralsConnectionsCard from "../../components/ReferralsConnectionsCard";
import { useSearchParams } from "react-router-dom";
import paths from "../../constants/paths";
import { useGeneralProfile } from "../../contexts/Organization";
import { ReactComponent as SearchIcon } from "../../assets/Icons/search.svg";
import { useAuth } from "../../contexts/Auth";
import {
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
  Avatar,
  Skeleton,
} from "@mui/material";
import { debounce, throttle } from "lodash";
import { useCallback } from "react";
import { trackClickEvent } from "../../mixpanel";
import { axiosInstance } from "../../services/axiosInstance";
import { CometChat } from "@cometchat/chat-sdk-javascript";

const SearchSkeleton = () => (
  <>
    {[...Array(4)].map((_, index) => (
      <ListItem key={index} sx={{ py: 2 }}>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ mr: 2, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="90%" height={16} />
          <Skeleton variant="text" width="70%" height={16} />
        </Box>
      </ListItem>
    ))}
  </>
);

const HomePage = () => {
  const {
    searchQuery,
    searchResults,
    isSearching,
    searchError,
    handleSearch,
    handleState,
    // Other context values
    fetchStatus,
    suggestPeople,
    fetchSuggestedPeople,
    fetchPosts,
    fetchPostsStatus,
    posts,
    createPostStatus,
    searchedPosts,
    searchTotal,
    searchPage,
    searchLimit,
    fetchSearchPosts,
    fetchMarketingData,
  } = useHomePage();

  const highlightSearchTerm = (text, searchTerm, limitLines = false) => {
    if (!searchTerm || !text) return text || "";

    // Common words to ignore from highlighting
    const IGNORE_WORDS = new Set([
      "the",
      "and",
      "or",
      "a",
      "an",
      "is",
      "of",
      "to",
      "in",
      "for",
    ]);

    if (!limitLines) {
      let result = "";
      let lastIndex = 0;
      const lowerText = text.toLowerCase();
      const lowerSearchTerm = searchTerm.toLowerCase();

      while (true) {
        const index = lowerText.indexOf(lowerSearchTerm, lastIndex);
        if (index === -1) break;

        // Add text before match
        result += text.substring(lastIndex, index);
        // Add highlighted match (using original case)
        const matchedText = text.substring(index, index + searchTerm.length);
        result += `<span style="background-color: #fff59d;">${matchedText}</span>`;

        lastIndex = index + searchTerm.length;
      }

      // Add remaining text
      result += text.substring(lastIndex);
      return result;
    }

    // Split search terms by comma, space, or other delimiters
    const searchTerms = searchTerm
      .split(/[,\s]+/)
      .filter(
        (term) => term.length > 1 && !IGNORE_WORDS.has(term.toLowerCase()),
      );

    if (searchTerms.length === 0) {
      // No important terms to highlight, show first 100 chars
      const limitedText = text.substring(0, 100);
      return limitedText + (text.length > 100 ? "..." : "");
    }

    const textLower = text.toLowerCase();
    const matches = [];

    // Find all occurrences of all search terms
    searchTerms.forEach((term) => {
      const termLower = term.toLowerCase();
      let index = textLower.indexOf(termLower);
      while (index !== -1) {
        matches.push({ index, term, length: term.length });
        index = textLower.indexOf(termLower, index + 1);
      }
    });

    if (matches.length === 0) {
      // Fallback: show first 100 characters
      const limitedText = text.substring(0, 100);
      return limitedText + (text.length > 100 ? "..." : "");
    }

    // Sort matches by position
    matches.sort((a, b) => a.index - b.index);

    const chunks = [];
    const contextSize = 30; // Reduced context size to make multiple snippets more readable

    // Process matches to create non-overlapping chunks
    let lastEnd = 0;
    matches.forEach((match, i) => {
      // Skip if this match is too close to previous one
      if (match.index < lastEnd) return;

      const start = Math.max(lastEnd, match.index - contextSize);
      const end = Math.min(
        text.length,
        match.index + match.length + contextSize,
      );

      let chunk = text.substring(start, end);

      // Add ellipses if we're skipping text
      if (start > lastEnd && lastEnd > 0) {
        chunk = "..." + chunk;
      }
      if (end < text.length && i < matches.length - 1) {
        chunk = chunk + "...";
      }

      chunks.push(chunk);
      lastEnd = end;
    });

    let result = chunks.join(" ");

    searchTerms.forEach((term) => {
      if (!IGNORE_WORDS.has(term.toLowerCase())) {
        let tempResult = "";
        let lastIndex = 0;
        const lowerText = result.toLowerCase();
        const lowerTerm = term.toLowerCase();

        while (true) {
          const index = lowerText.indexOf(lowerTerm, lastIndex);
          if (index === -1) break;

          // Add text before match
          tempResult += result.substring(lastIndex, index);
          // Add highlighted match (using original case)
          const matchedText = result.substring(index, index + term.length);
          tempResult += `<span style="background-color: #fff59d;">${matchedText}</span>`;

          lastIndex = index + term.length;
        }

        // Add remaining text
        tempResult += result.substring(lastIndex);
        result = tempResult;
      }
    });

    return result;
  };

  // Simple debounced search function
  const [debouncedTimeout, setDebouncedTimeout] = useState(null);

  const handleSearchInput = (query) => {
    clearTimeout(debouncedTimeout);
    if (query.length >= 2) {
      setDebouncedTimeout(
        setTimeout(() => {
          handleSearch(query);
        }, 500),
      );
    } else {
      handleState({ searchResults: [], isSearching: false });
    }
    trackClickEvent("Searched Posts", {
      component: "Search post",
      page: "Homepage",
    });
  };

  const { data, fetchInfo } = useBasicInfo();
  const { fetchFacilityInfo } = useGeneralProfile();
  const { account_type, isMentalHealthProfessional } = useAuth();
  const [searchParams] = useSearchParams();
  const post_id = searchParams.get("post_id");

  const [params, setParams] = useState({ page: 1 });
  const [loading, setLoading] = useState(false);
  const searchDropdownRef = useRef(null);
  const isSelectingResult = useRef(false);
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const [leftSidebarTop] = useState(80);
  const [rightSidebarTop] = useState(80);

  // Reset search state when clicking outside the search area
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchQuery.length >= 2 && !isSelectingResult.current) {
        const searchContainer = searchDropdownRef.current?.parentElement;
        if (searchContainer && !searchContainer.contains(event.target)) {
          handleState({
            searchQuery: "",
            searchResults: [],
            isSearching: false,
            searchError: null,
            searchTotal: 0,
            searchPage: 1,
          });
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [searchQuery]);

  useEffect(() => {
    const fetchMarketingEvent = async () => {
      try {
        const response = await fetchMarketingData();
      } catch (error) {
        console.error("Failed to fetch marketing data:", error);
      }
    };
    fetchMarketingEvent();
  }, []);

  // Sidebar scroll boundary logic
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      // Left sidebar logic
      if (leftSidebarRef.current) {
        const sidebarHeight = leftSidebarRef.current.scrollHeight;
        const minTop = viewportHeight - sidebarHeight;
        const newTop = Math.max(minTop, 80 - scrollY);
        const finalTop = Math.min(80, newTop);
        leftSidebarRef.current.style.top = `${finalTop}px`;
      }

      // Right sidebar logic
      if (rightSidebarRef.current) {
        const sidebarHeight = rightSidebarRef.current.scrollHeight;
        const minTop = viewportHeight - sidebarHeight;
        const newTop = Math.max(minTop, 80 - scrollY);
        const finalTop = Math.min(80, newTop);
        rightSidebarRef.current.style.top = `${finalTop}px`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Remove the infinite scroll related states and functions
  // const [hasMore, setHasMore] = useState(true);
  // const fetchNextPage = async () => {
  //   if (fetchPostsStatus === SUCCESS && hasMore) {
  //     const nextPage = params.page + 1;
  //     setParams((prev) => ({ ...prev, page: nextPage }));
  //     await fetchPosts({ page: nextPage, post_id, account_type });
  //   }
  // };

  // Function to handle "See more posts" button click
  const handleSeeMorePosts = async () => {
    if (fetchPostsStatus !== LOADING) {
      const nextPage = params.page + 1;
      setParams((prev) => ({ ...prev, page: nextPage }));
      await fetchPosts({ page: nextPage, post_id, account_type });
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchSuggestedPeople(
      account_type === treatment_program ? treatment_program : "user",
    );
    if (account_type === treatment_program && !isMentalHealthProfessional()) {
      fetchFacilityInfo();
    } else {
      fetchInfo();
    }
    setLoading(false);
  }, [account_type]);

  useEffect(() => {
    fetchPosts({ ...params, post_id, account_type });
  }, [params.page, account_type]);

  useEffect(() => {
    if (createPostStatus === SUCCESS) {
      setParams({ page: 1 });
      // Scroll to edited post after refresh
      const editedPostId = sessionStorage.getItem("editedPostId");
      if (editedPostId) {
        setTimeout(() => {
          const postElement = document.querySelector(
            `[data-post-id="${editedPostId}"]`,
          );
          if (postElement) {
            postElement.scrollIntoView({ behavior: "smooth", block: "center" });
            sessionStorage.removeItem("editedPostId");
          }
        }, 500);
      }
    }
  }, [createPostStatus]);

  // fetching cometchat token
  useEffect(()=>{
    (async ()=>{
      try {
        const response = await axiosInstance.get('/auth/cometchat')
        const cometchatToken = response.data.authToken
        const loggedInUser = await CometChat.login(cometchatToken);
        console.log("User authenticated securely:", loggedInUser);
      } catch (error) {
        console.log('error authenticating cometChat', error)
      }
    })()
  },[])

  // Remove the hasMore effect
  // useEffect(() => {
  //   if (posts?.last_page && params.page >= posts.last_page) {
  //     setHasMore(false);
  //   }
  // }, [posts, params.page]);

  const profileProgress = [
    true,
    true,
    Boolean(data?.info?.office_hours_time) ||
      Boolean(data?.info?.office_hours_day),
    (data?.client_types || [])?.length > 0,
    (data?.specialties || [])?.length > 0,
    (data?.treatment_modalities || [])?.length > 0,
    (data?.degrees || [])?.length > 0,
    (data?.fees || [])?.length > 0,
  ].filter((item) => item);

  return (
    <Container>
      <Grid container spacing={{ xs: 0, md: 3 }}>
        <Grid item xs={12} md={2.5}>
          <Box
            ref={leftSidebarRef}
            sx={{
              position: "sticky",
              top: leftSidebarTop,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              transition: "top 0.1s ease-out",
            }}
          >
            <Box
              sx={{
                display: { md: "none" },
              }}
            >
              {!loading &&
                profileProgress.length < 8 &&
                account_type !== treatment_program && (
                  <InviteCard />
                  // <ProfileProgressCard progress={profileProgress} />
                )}
            </Box>

            <TherapistInfo />
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {(account_type === treatment_program ||
                profileProgress?.length === 8) && <InviteCard />}
              {<MarketingCard />}
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {!Boolean(posts?.data?.length) && fetchPostsStatus !== LOADING && (
            <CreateAnnouncementCard />
          )}

          {!post_id && (
            <>
              <PostForm />
              <Paper
                sx={{
                  boxShadow: "none",
                  borderColor: "border.main",
                  p: 3,
                }}
              >
                <Box sx={{ width: "100%", position: "relative" }}>
                  <TextField
                    placeholder="Search posts by name or keyword"
                    multiline
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => {
                      const query = e.target.value;
                      handleState({
                        searchQuery: query,
                        isSearching: query.length >= 2,
                      });
                      handleSearchInput(query);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ width: 200 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        py: 1.6,
                        px: 2,
                        textarea: {
                          fontSize: 14,
                          lineHeight: "22px",
                          "&::placeholder": {
                            fontWeight: 700,
                          },
                        },
                      },
                    }}
                  />
                  {searchQuery.length >= 2 && (
                    <Paper
                      ref={searchDropdownRef}
                      sx={{
                        position: "absolute",
                        width: "100%",
                        backgroundColor: "background.paper",
                        boxShadow: 1,
                        borderRadius: 1,
                        maxHeight: "calc(100vh - 200px)",
                        overflow: "auto",
                        zIndex: 2,
                      }}
                    >
                      <List>
                        {isSearching ? (
                          <SearchSkeleton />
                        ) : searchResults.length === 0 ? (
                          <ListItem>
                            <ListItemText
                              primary={`No results found for '${searchQuery}'. Try using broader keywords, checking your spelling, or searching for related topics.`}
                            />
                          </ListItem>
                        ) : (
                          <>
                            <ListItem>
                              <ListItemText
                                primary={`Showing ${searchResults.length} of ${searchTotal} results for ${searchQuery}`}
                              />
                            </ListItem>
                            {searchResults.map((post) =>
                              post.first_name || post.facility_name ? (
                                <ListItem
                                  key={post.id}
                                  onMouseDown={() => {
                                    isSelectingResult.current = true;
                                    handleState({ searchQuery: "" });
                                    window.location.href = `${paths.post}/${post.id}`;
                                  }}
                                  onMouseUp={() => {
                                    isSelectingResult.current = false;
                                  }}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    cursor: "pointer",
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  <Avatar
                                    src={
                                      post.by_treatment_program
                                        ? post.facility_image
                                        : post.profile_image ||
                                          "/default-avatar.png"
                                    }
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                    }}
                                    alt={
                                      post.by_treatment_program
                                        ? post.facility_name
                                        : post.first_name
                                    }
                                  />
                                  <div
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchTerm(
                                          post.by_treatment_program
                                            ? post.facility_name
                                            : post.first_name +
                                                " " +
                                                post.last_name +
                                                `${
                                                  post?.degree
                                                    ? ", " + post.degree
                                                    : ""
                                                }`,
                                          searchQuery,
                                        ),
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={{
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        WebkitLineClamp: 1,
                                        overflow: "hidden",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchTerm(
                                          post.subject,
                                          searchQuery,
                                          true,
                                        ),
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        display: "-webkit-box",
                                        WebkitBoxOrient: "vertical",
                                        WebkitLineClamp: 2,
                                        overflow: "hidden",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: highlightSearchTerm(
                                          post.body,
                                          searchQuery,
                                          true,
                                        ),
                                      }}
                                    />
                                  </div>
                                </ListItem>
                              ) : null,
                            )}
                            {searchResults.length < searchTotal && (
                              <ListItem
                                button
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  isSelectingResult.current = true;
                                  handleSearch(searchQuery, searchPage + 1);
                                  setTimeout(() => {
                                    isSelectingResult.current = false;
                                  }, 100);
                                }}
                                sx={{
                                  textAlign: "center",
                                  py: 2,
                                }}
                              >
                                <ListItemText primary="Load more results" />
                              </ListItem>
                            )}
                          </>
                        )}
                      </List>
                    </Paper>
                  )}
                  {searchQuery && searchedPosts.data.length > 0 && (
                    <Paper
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        maxHeight: "300px",
                        overflowY: "auto",
                        zIndex: 1,
                        mt: 1,
                      }}
                    >
                      <List>
                        {searchedPosts.data.map((post) => (
                          <ListItem
                            key={post.id}
                            onClick={() => {
                              handleState({ searchQuery: "" });
                              window.location.href = `${paths.post}/${post.id}`;
                            }}
                          >
                            <ListItemText
                              primary={post.title}
                              secondary={post.body.substring(0, 100) + "..."}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </Paper>
            </>
          )}

          {/* Remove InfiniteScroll component and replace with regular rendering */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {fetchPostsStatus === LOADING && params.page === 1 ? (
              <>
                <CardLoader />
              </>
            ) : (
              posts?.data.map((item, index) => {
                if (index === 0) {
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <PostCard data={item} />
                      {post_id && (
                        <>
                          {suggestPeople.length > 0 && (
                            <Paper
                              sx={{
                                boxShadow: "none",
                                borderColor: "border.main",
                                p: 3,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              <Typography variant="h5">
                                Grow your network
                              </Typography>
                              {suggestPeople.map((item) => (
                                <UserVerticalCard key={item.id} data={item} />
                              ))}
                              <Link
                                href={paths.network(colleagues)}
                                variant="body1"
                                fontWeight={600}
                                color="text.primary"
                              >
                                Find more specialized therapists
                              </Link>
                            </Paper>
                          )}
                          <PostForm />
                        </>
                      )}
                    </Box>
                  );
                }
                return <PostCard data={item} key={item.id} />;
              })
            )}

            {/* Show loader when loading more posts */}
            {fetchPostsStatus === LOADING && params.page > 1 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {/* Show "See more posts" button if there are more pages */}
            {posts?.last_page && params.page < posts.last_page && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSeeMorePosts}
                  disabled={fetchPostsStatus === LOADING}
                  sx={{
                    alignSelf: "center",
                    width: "25%",
                    borderRadius: 5,
                    backgroundColor: "primary.main2",
                    "&:hover": {
                      backgroundColor: "primary.dark2",
                    },
                  }}
                >
                  {fetchPostsStatus === LOADING ? (
                    <CircularProgress size={24} />
                  ) : (
                    "See More Posts"
                  )}
                </Button>
              </Box>
            )}
            {posts?.last_page &&
              params.page == posts.last_page &&
              fetchPostsStatus !== LOADING && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={true}
                    sx={{
                      minWidth: 200,
                      fontWeight: 600,
                      fontSize: "1rem",
                    }}
                  >
                    No more posts to show
                  </Button>
                </Box>
              )}
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={2.5}
          sx={{
            display: { xs: "none", md: "block" },
          }}
        >
          <Box
            ref={rightSidebarRef}
            sx={{
              position: "sticky",
              top: rightSidebarTop,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {!loading &&
              profileProgress.length < 8 &&
              account_type !== treatment_program && (
                <InviteCard />
                // <ProfileProgressCard progress={profileProgress} />
              )}
            {(account_type === treatment_program ||
              profileProgress.length === 8) && <InviteCard />}
            {<MarketingCard />}
          </Box>
        </Grid>
      </Grid>
      <VerificationModal />
    </Container>
  );
};

export default HomePage;
